import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DrivingIcon from "@mui/icons-material/DirectionsCar";
import GroupIcon from "@mui/icons-material/Group";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NoteIcon from "@mui/icons-material/NoteAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import TransitIcon from "@mui/icons-material/Train";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEventListener } from "@wojtekmaj/react-hooks";
import axios, { AxiosError, type AxiosResponse } from "axios";
import { useNavigate, useParams } from "react-router-dom";

import type {
  AcceptInterviewInviteInput,
  ExtendedThalamosUser,
  InterviewVerification,
  RejectInterviewInviteInput,
  RouteTimeResult,
} from "@thalamos/aspire-common";
import {
  hasDoctorGmcNumber,
  INTERVIEW_INVITE_PREDEFINED_REJECTION_REASONS,
  isGuestUserSession,
  notificationChannelFns,
  NotificationEvents,
} from "@thalamos/aspire-common";

import { api } from "~/api.js";
import {
  Banner,
  BannerList,
} from "~/components/design-system/Banner/Banner.js";
import {
  ButtonIcon,
  Button as CustomButton,
} from "~/components/design-system/Button/Button.js";
import {
  renderInfoToast,
  renderSuccessToast,
} from "~/components/design-system/ToastNotifications/ToastNotifications.js";
import { config } from "~/config.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { useChannel } from "~/lib/pusher/useChannel.js";
import { useEvent } from "~/lib/pusher/useEvent.js";
import { routeFns } from "~/routePaths.js";
import { formatInterviewTimeRange } from "~/util.js";

// ---------------------------------------------------------------------------
// Axios error helpers
// ---------------------------------------------------------------------------

function extractAxiosErrorMessage(
  err: unknown,
  fallback: string,
): { status?: number; reason?: string; message: string } {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data as
      { reason?: string; message?: string } | undefined;
    return {
      status,
      reason: data?.reason,
      message: data?.message ?? data?.reason ?? fallback,
    };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: fallback };
}

/**
 * Refusals must reach the handlers as an `AxiosError` carrying the status and
 * body they inspect, even from a client configured to resolve them.
 */
function throwIfRefused(response: AxiosResponse): void {
  if (response.status >= 200 && response.status < 300) return;
  throw new AxiosError(
    `Request failed with status code ${String(response.status)}`,
    AxiosError.ERR_BAD_REQUEST,
    response.config,
    response.request,
    response,
  );
}

// ---------------------------------------------------------------------------
// Injectable API (for testing / Storybook)
// ---------------------------------------------------------------------------

/** API functions used by InterviewInviteResponsePage — injectable for testing */
export interface InterviewInviteResponsePageApi {
  getInvite: (shareCode: string) => Promise<{ data: InterviewVerification }>;
  acceptInvite: (
    shareCode: string,
    body?: AcceptInterviewInviteInput,
  ) => Promise<void>;
  rejectInvite: (
    shareCode: string,
    body: RejectInterviewInviteInput,
  ) => Promise<void>;
  getInviteRouteTime: (
    shareCode: string,
  ) => Promise<{ data: RouteTimeResult | null }>;
}

/** Default API implementation using the real API */
const defaultApi: InterviewInviteResponsePageApi = {
  getInvite: async (shareCode) => {
    const response = await api.interviews.getInvite(shareCode);
    throwIfRefused(response);
    return response;
  },
  acceptInvite: async (shareCode, body) => {
    throwIfRefused(await api.interviews.acceptInvite(shareCode, body));
  },
  rejectInvite: async (shareCode, body) => {
    throwIfRefused(await api.interviews.rejectInvite(shareCode, body));
  },
  getInviteRouteTime: (shareCode) =>
    api.interviews.getInviteRouteTime(shareCode),
};

/** Navigation functions that can be injected for testing */
export interface InterviewInviteResponsePageNavigation {
  navigateToHome: () => void;
  navigateToFormContext: (formContextId: string, patientId: string) => void;
}

// ---------------------------------------------------------------------------
// Status config (for non-valid verification states)
// ---------------------------------------------------------------------------

function getStatusConfig(): Record<
  Exclude<InterviewVerification["status"], "valid">,
  {
    bannerType: BannerList;
    title: string;
    message: string;
    showRedirectButton?: boolean;
    redirectLabel?: string;
  }
> {
  return {
    "already-responded": {
      bannerType: BannerList.INFO,
      title: "Already Responded",
      message: "You have already responded to this invitation.",
      showRedirectButton: true,
      redirectLabel: "Go to Assessment",
    },
    "already-has-access": {
      bannerType: BannerList.CONFIRMATION,
      title: "You Already Have Access",
      message:
        "You already have an active work item for this assessment. Redirecting you now...",
      showRedirectButton: true,
      redirectLabel: "Go to Assessment",
    },
    expired: {
      bannerType: BannerList.WARNING,
      title: "Invitation Expired",
      message:
        "This invitation has expired. Assessment invitations are valid for 7 days. Please contact the AMHP team if you still need access to this assessment.",
    },
  };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface InterviewInviteResponsePageProps {
  /** Optional share code override (for stories — otherwise uses URL param) */
  shareCodeOverride?: string;
  /** Optional API override for testing */
  apiOverride?: InterviewInviteResponsePageApi;
  /** Optional navigation override for testing */
  navigationOverride?: InterviewInviteResponsePageNavigation;
  /** Optional user override for testing (bypasses context) */
  userOverride?: ExtendedThalamosUser | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InterviewInviteResponsePage({
  shareCodeOverride,
  apiOverride,
  navigationOverride,
  userOverride,
}: InterviewInviteResponsePageProps = {}) {
  const { shareCode: shareCodeFromParams } = useParams<{
    shareCode: string;
  }>();
  const navigate = useNavigate();
  const currentUserContext = useContext(LoggedInUserContext);

  const shareCode = shareCodeOverride ?? shareCodeFromParams;
  const apiClient = apiOverride ?? defaultApi;
  const user =
    userOverride !== undefined ? userOverride : currentUserContext?.user;

  const navigation = useMemo<InterviewInviteResponsePageNavigation>(
    () =>
      navigationOverride ?? {
        navigateToHome: () => {
          navigate(routeFns.home());
        },
        navigateToFormContext: (formContextId, patientId) => {
          navigate(routeFns.formContextPage(formContextId, patientId));
        },
      },
    [navigationOverride, navigate],
  );

  const [isLoading, setIsLoading] = useState(true);
  const [verification, setVerification] =
    useState<InterviewVerification | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState("");
  const [customRejectionReason, setCustomRejectionReason] = useState("");

  const [travelMode, setTravelMode] = useState<"driving" | "transit">(
    "driving",
  );
  const [routeTime, setRouteTime] = useState<RouteTimeResult | null>(null);
  const [routeTimeLoading, setRouteTimeLoading] = useState(false);

  const [mapImageError, setMapImageError] = useState(false);

  const rejectSectionRef = useRef<HTMLDivElement>(null);

  const rejectionReason =
    selectedRejectionReason === "Other"
      ? customRejectionReason.trim()
      : selectedRejectionReason;

  // Auto-scroll to rejection form when it opens
  useEffect(() => {
    if (showRejectDialog) {
      // Allow Collapse animation to start before scrolling
      const timer = setTimeout(() => {
        rejectSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 300);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [showRejectDialog]);

  // Check user has a GMC number or is a guest user
  useEffect(() => {
    if (!user) return;

    if (!hasDoctorGmcNumber(user) && !isGuestUserSession(user)) {
      setError(
        "You must be logged in as a doctor with a GMC number to access this page",
      );
      setIsLoading(false);
    }
  }, [user]);

  // Set from the doctor's accept or decline until it fails: the server
  // announces their response as a change to the interview, and re-reading the
  // invite then would repaint the page as it navigates away
  const responding = useRef(false);

  const latestRead = useRef(0);

  const readInvite = useCallback(
    async (refresh: boolean) => {
      if (!shareCode) return;
      latestRead.current += 1;
      const read = latestRead.current;
      const superseded = () => read !== latestRead.current;
      try {
        const response = await apiClient.getInvite(shareCode);
        if (superseded() || (refresh && responding.current)) return;
        setVerification(response.data);
      } catch (err: unknown) {
        // A refresh that fails keeps the invite as last read
        if (refresh || superseded()) return;
        const axiosErr = extractAxiosErrorMessage(
          err,
          "Failed to load invitation",
        );
        if (axiosErr.status === 404) {
          setError(
            "Invitation not found. It may have expired or been revoked.",
          );
        } else {
          setError(axiosErr.message);
        }
      }
      setIsLoading(false);
    },
    [shareCode, apiClient],
  );

  // Verify the invite
  useEffect(() => {
    if (!shareCode || !user || error) return;
    void readInvite(false);
  }, [shareCode, user, error, readInvite]);

  const refreshInvite = useCallback(() => {
    if (isLoading || error || responding.current) return;
    void readInvite(true);
  }, [isLoading, error, readInvite]);

  const interviewsChannel = useChannel(
    verification
      ? notificationChannelFns.interviews(verification.formContextId)
      : undefined,
  );
  useEvent(interviewsChannel, NotificationEvents.resourceReload, refreshInvite);
  // Catches up on announcements missed while the device slept or was offline
  useEventListener(window, "focus", refreshInvite);
  useEventListener(document, "visibilitychange", () => {
    if (document.visibilityState === "visible") refreshInvite();
  });

  // Keyed on these fields rather than on `verification`, which every read
  // replaces, so a refresh that confirms the doctor's access does not restart
  // the redirect
  const hasAccess = verification?.status === "already-has-access";
  const formContextId = verification?.formContextId;
  const patientId = verification?.patientId;
  useEffect(() => {
    if (!hasAccess || !formContextId || !patientId) return undefined;
    renderInfoToast({
      message: "You already have access to this assessment. Redirecting...",
    });
    const timer = setTimeout(() => {
      navigation.navigateToFormContext(formContextId, patientId);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [hasAccess, formContextId, patientId, navigation]);

  // Fetch route time once the invite is valid, keyed on its fields so a
  // refresh does not fetch it again
  const isValid = verification?.status === "valid";
  const postalCode = verification?.postalCode;
  useEffect(() => {
    if (!shareCode || !user || !isValid || !postalCode) return;

    setRouteTimeLoading(true);
    apiClient
      .getInviteRouteTime(shareCode)
      .then((result) => {
        setRouteTime(result.data);
      })
      .catch(() => {
        // Silently fail — route time is a nice-to-have
      })
      .finally(() => {
        setRouteTimeLoading(false);
      });
  }, [shareCode, user, isValid, postalCode, apiClient]);

  const handleAccept = async () => {
    if (!shareCode || !verification) return;

    responding.current = true;
    setIsSubmitting(true);
    try {
      await apiClient.acceptInvite(shareCode, {
        selectedTimeRange: undefined,
      });

      // Navigate to the form context page
      navigation.navigateToFormContext(
        verification.formContextId,
        verification.patientId,
      );
    } catch (err: unknown) {
      const axiosErr = extractAxiosErrorMessage(
        err,
        "Failed to accept invitation",
      );
      if (axiosErr.status === 409) {
        setError(
          axiosErr.reason ?? "You have already responded to this invitation",
        );
      } else if (axiosErr.status === 410) {
        setError(axiosErr.reason ?? "Invitation is no longer valid");
      } else {
        setError(axiosErr.message);
      }
      responding.current = false;
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!shareCode) return;

    responding.current = true;
    setIsSubmitting(true);
    try {
      await apiClient.rejectInvite(shareCode, {
        rejectionReason: rejectionReason.trim() || undefined,
      });
      renderSuccessToast({ message: "Invitation declined successfully" });
      navigation.navigateToHome();
    } catch (err: unknown) {
      const axiosErr = extractAxiosErrorMessage(
        err,
        "Failed to decline invitation",
      );
      if (axiosErr.status === 409) {
        setError(
          axiosErr.reason ?? "You have already responded to this invitation",
        );
      } else if (axiosErr.status === 410) {
        setError(axiosErr.reason ?? "Invitation is no longer valid");
      } else {
        setError(axiosErr.message);
      }
      responding.current = false;
      setIsSubmitting(false);
      setShowRejectDialog(false);
    }
  };

  const handleNavigateToAssessment = () => {
    if (verification) {
      navigation.navigateToFormContext(
        verification.formContextId,
        verification.patientId,
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Error
  // ---------------------------------------------------------------------------

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 4, px: { xs: 2, sm: 3 } }}>
        <Banner bannerType={BannerList.ERROR} title="Error" sx={{ mb: 3 }} />
        <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
          {error}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CustomButton
            label="Return to Home"
            variant="outlined"
            onClick={() => {
              navigation.navigateToHome();
            }}
          />
        </Box>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Non-valid statuses (already-responded, already-has-access, revoked)
  // ---------------------------------------------------------------------------

  if (verification && verification.status !== "valid") {
    // Revoked invitations get a dedicated friendly message
    if (verification.existingStatus === "revoked") {
      return (
        <Box sx={{ maxWidth: 600, mx: "auto", mt: 4, px: { xs: 2, sm: 3 } }}>
          <Banner
            bannerType={BannerList.WARNING}
            title="Invitation Withdrawn"
            sx={{ mb: 3 }}
          />
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                This assessment invitation for{" "}
                <strong>{verification.patientName}</strong> has been withdrawn
                by the AMHP team.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                This usually means that another doctor has been confirmed for
                this assessment. No further action is required from you.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CustomButton
                  label="Return to Home"
                  variant="outlined"
                  onClick={() => {
                    navigation.navigateToHome();
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }

    const statusConfig = getStatusConfig()[
      verification.status as Exclude<InterviewVerification["status"], "valid">
    ] ?? {
      bannerType: BannerList.WARNING,
      title: "Invitation Unavailable",
      message:
        "This invitation is no longer available. It may have expired or been withdrawn.",
      showRedirectButton: false,
    };
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 4, px: { xs: 2, sm: 3 } }}>
        <Banner
          bannerType={statusConfig.bannerType}
          title={statusConfig.title}
          sx={{ mb: 3 }}
        />
        <Card elevation={2}>
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {statusConfig.message}
            </Typography>

            {/* Show invite details for context */}
            {(verification.patientName ||
              (verification.participantTeamNames?.length ?? 0) > 0) && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "grey.100",
                  borderRadius: 1,
                }}
              >
                {(verification.participantTeamNames?.length ?? 0) > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    <strong>Participants:</strong>{" "}
                    {verification.participantTeamNames.join(", ")}
                  </Typography>
                )}
                {verification.patientName && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    <strong>Patient:</strong> {verification.patientName}
                  </Typography>
                )}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              {statusConfig.showRedirectButton && (
                <CustomButton
                  label={statusConfig.redirectLabel ?? "Go to Assessment"}
                  variant="contained"
                  color="primary"
                  onClick={handleNavigateToAssessment}
                  startIcon={ButtonIcon.create}
                  testId="go-to-assessment-button"
                />
              )}
              <CustomButton
                label="Return to Home"
                variant="outlined"
                onClick={() => {
                  navigation.navigateToHome();
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!verification) return null;

  // ---------------------------------------------------------------------------
  // Valid invite — build map & location data
  // ---------------------------------------------------------------------------

  const assessmentLocation =
    verification.address && verification.postalCode
      ? `${verification.address}, ${verification.postalCode}`
      : (verification.postalCode ?? null);

  const doctorPostalCode = user?.homeBaseAddress?.postalCode ?? null;

  const selectedPolyline =
    travelMode === "driving"
      ? routeTime?.drivingPolyline
      : routeTime?.transitPolyline;

  const staticMapUrl =
    assessmentLocation && config.googleMapsApiKey !== ""
      ? (() => {
          const params = new URLSearchParams({
            size: "600x300",
            scale: "2",
            key: config.googleMapsApiKey,
            maptype: "roadmap",
          });
          params.append("markers", `color:red|label:A|${assessmentLocation}`);
          if (doctorPostalCode) {
            params.append(
              "markers",
              `color:green|label:H|${doctorPostalCode}, UK`,
            );
          }
          if (selectedPolyline) {
            params.append(
              "path",
              `color:0x4285F4FF|weight:4|enc:${selectedPolyline}`,
            );
          }
          return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
        })()
      : null;

  const directionsUrl =
    assessmentLocation && doctorPostalCode
      ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(`${doctorPostalCode}, UK`)}&destination=${encodeURIComponent(assessmentLocation)}&travelmode=${travelMode}`
      : assessmentLocation
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assessmentLocation)}`
        : null;

  const isPendingConfirmed =
    verification.existingStatus === "pending-confirmed";

  return (
    <Box
      sx={{
        maxWidth: 600,
        mx: "auto",
        mt: 4,
        px: { xs: 2, sm: 3 },
        pb: { xs: 4, sm: 2 },
      }}
    >
      <Card elevation={2}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            You have been requested to conduct a MHA assessment.
          </Typography>

          {/* Pending-confirmed banner */}
          {isPendingConfirmed && (
            <Banner
              bannerType={BannerList.CONFIRMATION}
              title="Preferred Doctor"
              body="You have been selected as the preferred doctor for this assessment. Please confirm your availability."
              sx={{ mb: 3 }}
            />
          )}

          {/* Static Map */}
          {assessmentLocation && (
            <Box sx={{ mb: 3 }}>
              {staticMapUrl && !mapImageError && (
                <Box
                  component={directionsUrl ? "a" : "div"}
                  href={directionsUrl ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "block",
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    cursor: directionsUrl ? "pointer" : "default",
                    textDecoration: "none",
                    "&:hover": directionsUrl
                      ? { borderColor: "primary.main" }
                      : {},
                  }}
                >
                  <Box
                    component="img"
                    src={staticMapUrl}
                    alt={`Map showing assessment at ${assessmentLocation}`}
                    onError={() => {
                      setMapImageError(true);
                    }}
                    sx={{ width: "100%", height: "auto", display: "block" }}
                  />
                </Box>
              )}

              {/* Travel mode toggle + time + directions link */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                  mt: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <ToggleButtonGroup
                    value={travelMode}
                    exclusive
                    onChange={(_, value: string | null) => {
                      if (value === "driving" || value === "transit") {
                        setTravelMode(value);
                      }
                    }}
                    size="small"
                    data-testid="travel-mode-toggle"
                  >
                    <ToggleButton
                      value="driving"
                      aria-label="Driving"
                      data-testid="travel-mode-driving"
                    >
                      <DrivingIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                      value="transit"
                      aria-label="Public transport"
                      data-testid="travel-mode-transit"
                    >
                      <TransitIcon fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {routeTimeLoading && (
                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={24}
                      sx={{ borderRadius: 2 }}
                    />
                  )}
                  {!routeTimeLoading &&
                    routeTime?.drivingTimeMinutes != null && (
                      <Chip
                        icon={<DrivingIcon sx={{ fontSize: "0.875rem" }} />}
                        label={`~${String(routeTime.drivingTimeMinutes)} min`}
                        size="small"
                        variant={
                          travelMode === "driving" ? "filled" : "outlined"
                        }
                        color="info"
                        sx={{
                          height: 24,
                          "& .MuiChip-label": {
                            px: 0.75,
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                    )}
                  {!routeTimeLoading &&
                    routeTime?.transitTimeMinutes != null && (
                      <Chip
                        icon={<TransitIcon sx={{ fontSize: "0.875rem" }} />}
                        label={`~${String(routeTime.transitTimeMinutes)} min`}
                        size="small"
                        variant={
                          travelMode === "transit" ? "filled" : "outlined"
                        }
                        color="info"
                        sx={{
                          height: 24,
                          "& .MuiChip-label": {
                            px: 0.75,
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                    )}
                </Box>

                {directionsUrl && (
                  <Button
                    size="small"
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    sx={{ textTransform: "none", fontSize: "0.8rem" }}
                    data-testid="get-directions-link"
                  >
                    Get Directions
                  </Button>
                )}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Assessment details */}
          <Box sx={{ mb: 4 }}>
            {verification.amhpName && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2.5,
                }}
              >
                <PersonIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "text.secondary",
                    mt: 0.25,
                  }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 0.25 }}
                  >
                    AMHP
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "text.primary", fontWeight: 500 }}
                  >
                    {verification.amhpName}
                  </Typography>
                  {verification.amhpPhone && (
                    <Chip
                      icon={<PhoneIcon />}
                      label={verification.amhpPhone}
                      component="a"
                      href={`tel:${verification.amhpPhone.replace(/\s/g, "")}`}
                      clickable
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ mt: 0.75 }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {!verification.amhpName &&
              verification.participantTeamNames.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    mb: 2.5,
                  }}
                >
                  <GroupIcon
                    sx={{
                      fontSize: "1.25rem",
                      color: "text.secondary",
                      mt: 0.25,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "text.secondary", mb: 0.25 }}
                    >
                      AMHP Team
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "text.primary", fontWeight: 500 }}
                    >
                      {verification.participantTeamNames.join(", ")}
                    </Typography>
                  </Box>
                </Box>
              )}

            {verification.noteToDoctor && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2.5,
                }}
                data-testid="note-from-amhp"
              >
                <NoteIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "text.secondary",
                    mt: 0.25,
                  }}
                />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 0.25 }}
                  >
                    Note from AMHP
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {verification.noteToDoctor}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                mb: 2.5,
              }}
            >
              <PersonIcon
                sx={{ fontSize: "1.25rem", color: "text.secondary", mt: 0.25 }}
              />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary", mb: 0.25 }}
                >
                  Patient
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: 500 }}
                >
                  {verification.patientName}
                </Typography>
              </Box>
            </Box>

            {assessmentLocation && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2.5,
                }}
              >
                <LocationOnIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "text.secondary",
                    mt: 0.25,
                  }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 0.25 }}
                  >
                    Assessment Location
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "text.primary", fontWeight: 500 }}
                  >
                    {verification.address}
                    {verification.postalCode && `, ${verification.postalCode}`}
                  </Typography>
                </Box>
              </Box>
            )}

            {verification.locationUnknown && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2.5,
                }}
              >
                <LocationOnIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "text.secondary",
                    mt: 0.25,
                  }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 0.25 }}
                  >
                    Assessment Location
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "text.primary", fontWeight: 500 }}
                  >
                    To be confirmed
                  </Typography>
                </Box>
              </Box>
            )}

            {verification.timeRanges.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2.5,
                }}
                data-testid="proposed-times"
              >
                <CalendarMonthIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "text.secondary",
                    mt: 0.25,
                  }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 0.25 }}
                  >
                    Proposed Times
                  </Typography>
                  {verification.timeRanges.map((range, idx) => (
                    <Typography
                      key={idx}
                      variant="body1"
                      sx={{
                        color: "text.primary",
                        fontWeight: 500,
                        mb: 0.25,
                      }}
                    >
                      {formatInterviewTimeRange(range.start, range.end)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {verification.otherAcceptedDoctors.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mt: 1,
                }}
              >
                <GroupIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "text.secondary",
                    mt: 0.25,
                  }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 0.25 }}
                  >
                    Other doctors attending
                  </Typography>
                  {verification.otherAcceptedDoctors.map((doctor, idx) => (
                    <Typography
                      key={idx}
                      variant="body1"
                      sx={{ color: "text.primary", fontWeight: 500, mb: 0.25 }}
                    >
                      {doctor.name}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {!showRejectDialog && (
            <>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Would you like to accept or decline this invitation?
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <CustomButton
                  label="Decline Invitation"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setShowRejectDialog(true);
                  }}
                  disabled={isSubmitting}
                  startIcon={ButtonIcon.close}
                  testId="decline-invite-button"
                  sx={{ flex: 1 }}
                />
                <CustomButton
                  label={isSubmitting ? "Accepting…" : "Start Assessment"}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    void handleAccept();
                  }}
                  disabled={isSubmitting}
                  startIcon={ButtonIcon.create}
                  testId="accept-invite-button"
                  sx={{ flex: 1 }}
                />
              </Box>
            </>
          )}

          {/* Rejection reason form */}
          <Collapse in={showRejectDialog} unmountOnExit>
            <Box ref={rejectSectionRef}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Decline Invitation
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please select a reason for declining this invitation:
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="rejection-reason-label">Reason</InputLabel>
                <Select
                  labelId="rejection-reason-label"
                  value={selectedRejectionReason}
                  label="Reason"
                  onChange={(e) => {
                    setSelectedRejectionReason(e.target.value);
                  }}
                  inputProps={{
                    "data-testid": "rejection-reason-select",
                  }}
                >
                  {INTERVIEW_INVITE_PREDEFINED_REJECTION_REASONS.map(
                    (reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
              {selectedRejectionReason === "Other" && (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={customRejectionReason}
                  onChange={(e) => {
                    setCustomRejectionReason(e.target.value);
                  }}
                  placeholder="Please provide details..."
                  helperText={`${String(customRejectionReason.length)} / 1,000`}
                  sx={{ mb: 2 }}
                  slotProps={{
                    formHelperText: { sx: { textAlign: "right" } },
                    htmlInput: {
                      "data-testid": "rejection-reason-input",
                      maxLength: 1000,
                    },
                  }}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                  mt: 3,
                }}
              >
                <CustomButton
                  label="Cancel"
                  variant="outlined"
                  onClick={() => {
                    setShowRejectDialog(false);
                    setSelectedRejectionReason("");
                    setCustomRejectionReason("");
                  }}
                  disabled={isSubmitting}
                  sx={{ flex: 1 }}
                />
                <CustomButton
                  label={isSubmitting ? "Declining…" : "Confirm Decline"}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    void handleReject();
                  }}
                  disabled={isSubmitting || !rejectionReason}
                  startIcon={ButtonIcon.close}
                  testId="confirm-decline-button"
                  sx={{
                    flex: 1,
                    backgroundColor: "error.main",
                    "&:hover": { backgroundColor: "error.dark" },
                  }}
                />
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
}
